import { useCallback, useRef, useState } from "react";
import type { BootstrapContext, BootstrapStatus, BootstrapStep } from "./types";

/** Результат {@link useAppBootstrap}. */
export interface UseAppBootstrapResult {
    status: BootstrapStatus;
    currentStep: BootstrapStep | null;
    error: Error | null;
    run: (steps: BootstrapStep[]) => Promise<void>;
}

/**
 * Последовательно выполняет шаги bootstrap и отдаёт состояние для UI.
 */
export function useAppBootstrap(): UseAppBootstrapResult {
    const [status, setStatus] = useState<BootstrapStatus>("idle");
    const [currentStep, setCurrentStep] = useState<BootstrapStep | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const runningRef = useRef(false);

    const run = useCallback(async (steps: BootstrapStep[]) => {
        if (runningRef.current) {
            return;
        }
        runningRef.current = true;
        setError(null);
        setStatus("running");

        const ctx: BootstrapContext = {};

        try {
            for (const step of steps) {
                setCurrentStep(step);
                await step.execute(ctx);
            }
            setStatus("success");
            setCurrentStep(null);
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            setStatus("error");
        } finally {
            runningRef.current = false;
        }
    }, []);

    return { status, currentStep, error, run };
}
